import { useCallback, useMemo } from 'react'
import { AnalyticsEvent, type ProductViewed } from '@shipt/analytics-member-web'
import { type ShiptEventOptions } from '@shipt/analytics-core'
import {
  type SearchParams,
  type GlobalSearchResponse,
  type SearchCategory,
  type SearchResults,
  type Beacons,
} from '@/features/shop/services/Search/types'
import { track } from '@/features/shared/analytics/trackingQueue'
import { urlPathnameToLocationName } from '@/features/shared/analytics/utils'
import { type DISPLAY_FORMAT } from '@/features/shared/constants/analytics'
import { useCurrentActiveOrderId } from '@/features/shop/state/SelectedActiveOrder/hooks'
import { findCurrentCategoryByParams } from '@/features/shop/services/Search/utils'
import { useIsPage } from '@/features/shared/hooks/useIsPage'
import { getTotalListCost } from '@/features/shop/components/BuyAgainPage/utils'
import { trackElementClicked } from '@/features/shared/analytics/element'
import { type GlobalStoreParams } from '@shared/types'
import { type ProductReviewStats } from '@/features/shop/services/Shelves/types'
import { type ProductForPriceFormatting } from '@/features/shop/components/VerticalProduct/utils'
import { useValidSearchParams } from '@/features/shared/hooks/search'
import { type VariationProperty } from '@/features/shop/services/Product/types'

type ProductLabel = 'bogo' | 'buy_again' | 'featured' | 'on_sale' | 'for_you' | 'out_of_stock'
type ProductBackendLabel = 'low_stock' | 'out_of_stock'

type ProductFilterParams = Pick<
  ProductViewed['properties'],
  | 'search_term'
  | 'search_type'
  | 'category_id'
  | 'brand_filters_applied'
  | 'sort_by_filters_applied'
  | 'active_deals_filter_applied'
  | 'store_id_filters_applied'
>
type ProductForProductLabels = {
  onSale: boolean
  bought: boolean
  bogo: boolean
  featured: boolean
  for_you?: boolean
  oos: boolean
}

const getProductLabels = (product: ProductForProductLabels): ProductLabel[] => {
  const productLabels: ProductLabel[] = []
  const { onSale, bought, bogo, featured, for_you, oos } = product

  if (onSale) productLabels.push('on_sale')
  if (bought) productLabels.push('buy_again')
  if (bogo) productLabels.push('bogo')
  if (featured) productLabels.push('featured')
  if (for_you) productLabels.push('for_you')
  if (oos) productLabels.push('out_of_stock')

  return productLabels
}

type ProductForProductBackendLabels = {
  lowStock: boolean
  oos: boolean
}
const getProductBackendLabels = (
  product: ProductForProductBackendLabels,
): ProductBackendLabel[] | undefined => {
  const productBackendLabels: ProductBackendLabel[] = []
  const { lowStock, oos } = product

  if (lowStock) productBackendLabels.push('low_stock')
  if (oos) productBackendLabels.push('out_of_stock')

  return productBackendLabels.length ? productBackendLabels : undefined
}

export const trackGlobalProductsSearched = (
  searchResults: GlobalSearchResponse,
  query: string,
  searchType: string,
  category_id?: string,
  category_name?: string,
) => {
  const productsAvailable =
    searchResults.retailer_hits?.reduce(
      (totalProducts, retailer) => totalProducts + retailer.total,
      0,
    ) ?? 0

  track({
    eventName: AnalyticsEvent.ProductsSearched,
    properties: {
      products_available: productsAvailable,
      page_number: 1,
      query_type: searchType || 'typed',
      query_term: query,
      no_results: searchResults.total_retailer_hits === 0,
      location: urlPathnameToLocationName(),
      category_id,
      category_name,
    },
  })
}

export type ProductEvent = {
  product: ProductForEvent
  displayFormat: DISPLAY_FORMAT
  gridIndex?: number
  listIndex?: number
  subLocation?: string
  filterParams?: ProductFilterParams
  shelfIndex?: number
  shelfCategory?: string
  shelfCategoryIndex?: number
  searchTerm?: string
  storeParams?: GlobalStoreParams
  categoryName?: string
  categoryId?: number
  searchType?: string
  featured_transaction_id?: string
  review_stats?: ProductReviewStats
  promotionId?: string
  promotionDisplayed?: boolean
  displayedPromotionType?: string
  ad_placement_id?: string
  requestedProductDiscountedPrice?: string
}
type ProductRemovedEvent = ProductEvent & {
  quantity_removed?: number
}
type ProductAddedEvent = ProductEvent & {
  quantity_added?: number
}
type TrackSelectProductArgs = ProductEvent & {
  newQuantity: number
  currentQuantity: number
}

export const trackProductViewed = (eventProperties: ProductEvent) => {
  const viewBeacon = eventProperties.product.beacons?.onViewBeacon

  track({
    eventName: AnalyticsEvent.ProductViewed,
    properties: {
      ...getProductEventProperties(eventProperties),
      ...(viewBeacon && {
        beacon_type: 'onViewBeacon',
        beacon_url: viewBeacon,
      }),
    },
  })
}

export const useTrackProduct = () => {
  const orderId = useCurrentActiveOrderId()

  const trackProductAdded = useCallback(
    (eventProperties: ProductAddedEvent) => {
      const { product, quantity_added } = eventProperties
      const properties = {
        ...getProductEventProperties(eventProperties),
        quantity_added: quantity_added || 1,
      }

      orderId
        ? track({
            eventName: AnalyticsEvent.ProductAddedToOrder,
            properties: { ...properties, order_id: String(orderId) },
          })
        : track({
            eventName: AnalyticsEvent.ProductAddedToCart,
            properties: {
              ...properties,
              image_url: product.image.url,
              ...(product.beacons?.onBasketChangeBeacon && {
                beacon_type: 'onBasketChangeBeacon',
                beacon_url: product.beacons.onBasketChangeBeacon,
              }),
            },
          })
    },
    [orderId],
  )

  const trackProductRemoved = useCallback(
    (eventProperties: ProductRemovedEvent) => {
      const properties = {
        ...getProductEventProperties(eventProperties),
        quantity_removed: eventProperties.quantity_removed || 1,
      }
      orderId
        ? track({
            eventName: AnalyticsEvent.ProductRemovedFromOrder,
            properties: { ...properties, order_id: String(orderId) },
          })
        : track({
            eventName: AnalyticsEvent.ProductRemovedFromCart,
            properties,
          })
    },
    [orderId],
  )

  return { trackProductAdded, trackProductRemoved }
}

export const trackProductClicked = (
  event: ProductEvent,
  eventOptions?: Partial<ShiptEventOptions>,
) => {
  const clickBeacon = event.product.beacons?.onClickBeacon
  track({
    eventName: AnalyticsEvent.ProductClicked,
    properties: {
      ...getProductEventProperties(event),
      ...(clickBeacon && {
        beacon_type: 'onClickBeacon',
        beacon_url: clickBeacon,
      }),
    },
    // for clicks that hard nav to another page, pass sendImmediately or the event and beacon get dropped
    ...(eventOptions && { eventOptions }),
  })
}

export const useTrackProductWithDropdown = () => {
  const { trackProductAdded, trackProductRemoved } = useTrackProduct()

  return ({ newQuantity, currentQuantity, ...eventProperties }: TrackSelectProductArgs) =>
    newQuantity > currentQuantity
      ? trackProductAdded({
          ...eventProperties,
          quantity_added: newQuantity - currentQuantity,
        })
      : trackProductRemoved({
          ...eventProperties,
          quantity_removed: currentQuantity - newQuantity,
        })
}

export const trackProductsSearched = (
  searchResults: SearchResults,
  params: SearchParams & { searchType?: string },
) => {
  const { page, total_hits, facets } = searchResults
  const { query, promotion_category_id, buy_again_query, searchType = 'typed' } = params
  const currentCategory = findCurrentCategoryByParams(facets, params)
  const hasNextLocation = searchResults.metadata?.redirect_url
  track({
    eventName: AnalyticsEvent.ProductsSearched,
    properties: {
      query_type: (query ?? buy_again_query) ? searchType : 'browse',
      products_available: total_hits || 0,
      page_number: page || 0,
      no_results: hasNextLocation ? false : total_hits === 0,
      location: urlPathnameToLocationName(),
      next_location: searchResults.metadata?.redirect_url,
      query_term: query ?? buy_again_query ?? undefined,
      ...(Boolean(promotion_category_id) && {
        promotion_id: String(promotion_category_id),
      }),
      ...(currentCategory?.id && {
        category_id: String(currentCategory.id),
        category_name: currentCategory.name,
      }),
    },
  })
}

export const useProductFiltersParams = (currentCategory?: SearchCategory) => {
  const searchParams = useValidSearchParams()
  const { SEARCH: isSearchPage, BUY_AGAIN: isBuyAgainPage } = useIsPage()
  return useMemo<ProductFilterParams>(() => {
    const { sort, query, buy_again_query, brands, active_deals, contexts } = searchParams

    return {
      brand_filters_applied: brands?.length ? brands : undefined,
      sort_by_filters_applied: sort ? [sort] : undefined,
      category_id: currentCategory?.id,
      category_name: currentCategory?.name,
      ...((isSearchPage || isBuyAgainPage) && {
        search_type: (query ?? buy_again_query) ? 'search' : 'browse',
        search_term: query ?? buy_again_query ?? '',
      }),
      // contexts is in the format of l7728_m245_s60 or [l7728_m245_s60, l7728_m245_s61]
      ...(contexts && {
        store_id_filters_applied: contexts.map((context) =>
          context.slice(context.indexOf('s') + 1),
        ),
      }),
      active_deals_filter_applied: Boolean(active_deals),
    }
  }, [searchParams, isSearchPage, isBuyAgainPage, currentCategory])
}

export const trackStoreResultsViewed = (query: string, searchType: string) => {
  track({
    eventName: AnalyticsEvent.StoreResultsViewed,
    properties: {
      location: urlPathnameToLocationName(),
      search_term: query,
      search_type: searchType,
    },
  })
}

type ProductForRequestedProductProperties = {
  id: number
  name: string
  brandName: string
  price: number
  universal_id: string
  onSale: boolean
  salePrice: number
  parentProductId?: number | null
  variationSize?: string | null
  variationColor?: string | null
  variationProperties?: VariationProperty[] | null
}
export const getRequestedProductProperties = (product: ProductForRequestedProductProperties) => ({
  requested_product_id: String(product.id),
  requested_product_name: product.name,
  requested_product_brand_name: product.brandName || '',
  requested_product_price: String(product.price),
  requested_universal_product_id: String(product.universal_id),
  ...(product.onSale && {
    requested_product_discounted_price: String(product.salePrice),
  }),
  // variation properties
  requested_product_variation_flag: Boolean(product.parentProductId),
  ...(product.parentProductId && {
    requested_product_parent_id: product.parentProductId,
  }),
  ...(product.variationSize && {
    requested_product_variation_size: product.variationSize,
  }),
  ...(product.variationColor && {
    requested_product_variation_color: product.variationColor,
  }),
  ...(product.variationProperties && {
    requested_product_variation_properties: product.variationProperties,
  }),
})

type ProductForProductEvent = {
  couponId: string
  cpgPromotion?: { category_id: number } | null
  featured: boolean
  featuredSource?: string | null
  featuredTransactionId?: string
  search_boost_aliases?: string[]
  review_stats?: ProductReviewStats
  image: { url: string }
  beacons?: Beacons
  boostBought?: boolean
  externalBought?: boolean
}
export type ProductForEvent = ProductForProductEvent &
  ProductForProductLabels &
  ProductForRequestedProductProperties &
  ProductForProductBackendLabels
export const getProductEventProperties = ({
  product,
  displayFormat,
  gridIndex,
  listIndex,
  subLocation,
  filterParams,
  shelfIndex,
  shelfCategory,
  shelfCategoryIndex,
  searchTerm,
  storeParams,
  categoryName,
  categoryId,
  searchType,
  featured_transaction_id,
  promotionId,
  promotionDisplayed,
  displayedPromotionType,
  ad_placement_id,
  requestedProductDiscountedPrice,
}: ProductEvent): ProductViewed['properties'] & {
  ad_placement_id?: string
} => {
  const productLabels = getProductLabels(product)
  const getSearchBoostedReason = () => {
    if (product.boostBought) return 'buy_again'
    if (product.search_boost_aliases?.length) return 'seasonal'
  }
  const {
    couponId,
    cpgPromotion,
    featured,
    featuredSource,
    featuredTransactionId,
    search_boost_aliases,
    review_stats,
    externalBought,
  } = product

  return {
    location: urlPathnameToLocationName(),
    display_format: displayFormat,
    ...(subLocation && { display_sublocation: subLocation }),
    grid_index: gridIndex,
    list_index: listIndex,
    shelf_index: shelfIndex,
    ...(shelfCategory !== undefined && {
      shelf_category: shelfCategory,
    }),
    ...(shelfCategoryIndex !== undefined && {
      shelf_category_index: shelfCategoryIndex,
    }),
    ...getRequestedProductProperties(product),
    ...(requestedProductDiscountedPrice !== undefined && {
      requested_product_discounted_price: requestedProductDiscountedPrice,
    }),
    frontend_product_labels: productLabels.length ? productLabels : undefined,
    backend_product_labels: getProductBackendLabels(product),
    // promotion properties
    promotion_displayed: promotionDisplayed ?? Boolean(cpgPromotion?.category_id || couponId),
    ...(displayedPromotionType && {
      displayed_promotion_type: displayedPromotionType,
    }),
    ...(couponId && {
      coupon_id: couponId,
      displayed_promotion_type: 'coupon',
    }),
    ...(cpgPromotion?.category_id && {
      promotion_id: String(cpgPromotion.category_id),
      displayed_promotion_type: 'featured_promotion',
    }),
    ...(featured && featuredSource && { featured_source: featuredSource }),
    featured_transaction_id: featuredTransactionId,
    search_boosted: Boolean(search_boost_aliases?.length),
    search_boosted_reason: getSearchBoostedReason(),
    ...filterParams,
    ...(searchTerm && {
      search_term: searchTerm,
    }),
    ...(categoryName && {
      category_name: categoryName,
    }),
    ...(categoryId && {
      category_id: categoryId,
    }),
    ...(searchType && {
      search_type: searchType,
    }),
    ...storeParams,
    ...(featured_transaction_id && {
      featured_transaction_id,
    }),
    ...(!!review_stats?.rating_distribution && {
      average_rating: review_stats.average_rating,
      rating_distribution: review_stats.rating_distribution,
      total_reviews: review_stats.total_reviews,
      recommendation_percentage: review_stats.recommended_percentage,
    }),
    external_bought: externalBought,
    ...(promotionId && {
      promotion_id: promotionId,
    }),
    ad_placement_id,
  }
}

export const trackProductAddedToList = (
  eventProperties: ProductAddedEvent & {
    listName: string
    listId: string
  },
) => {
  const { product, quantity_added, listName, listId } = eventProperties

  track({
    eventName: AnalyticsEvent.ProductAddedToList,
    properties: {
      ...getProductEventProperties(eventProperties),
      quantity_added: quantity_added || 1,
      image_url: product.image.url,
      list_name: String(listName),
      ...(listId && { list_id: listId }),
    },
  })
}

export const trackProductRemovedFromList = (
  eventProperties: ProductRemovedEvent & {
    listName: string
  },
) => {
  const { product, quantity_removed, listName } = eventProperties

  track({
    eventName: AnalyticsEvent.ProductRemovedFromList,
    properties: {
      ...getProductEventProperties(eventProperties),
      image_url: product.image.url,
      list_name: String(listName),
      // Discussed and approved via DS Stakeholder due to the type being incorrect from Analytics library
      quantity_added: quantity_removed || 1,
    },
  })
}

export const trackBulkProductAddedToCart = (
  list: { id: string; name: string; products: ProductForPriceFormatting[] },
  buttonLocation: string,
  buttonContent: string,
) => {
  trackElementClicked({
    content: buttonContent,
    type: 'button',
  })

  track({
    eventName: AnalyticsEvent.BulkProductAddedToCart,
    properties: {
      location: urlPathnameToLocationName(),
      products: list.products,
      display_sublocation: buttonLocation,
      list_name: list.name,
      list_id: list.id,
      total_price: Number(getTotalListCost(list.products).totalPrice),
      total_quantity: list.products.length,
    },
  })
}

export const trackBulkProductAddedToList = ({
  products,
  listName,
  listId,
  buttonLocation,
}: {
  products: ProductForPriceFormatting[]
  listName: string
  listId?: string
  buttonLocation?: string
}) => {
  track({
    eventName: AnalyticsEvent.BulkProductAddedToList,
    properties: {
      location: urlPathnameToLocationName(),
      products,
      display_sublocation: buttonLocation || '',
      list_name: listName,
      list_id: listId,
      total_price: Number(getTotalListCost(products).totalPrice),
      total_quantity: products.length,
    },
  })
}
